import React, { useState, useEffect } from "react";
import "./QuotationHistory.css";

function QuotationHistory() {
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOption, setSortOption] = useState("newest");

  useEffect(() => {
    fetchQuotations();
  }, []);

  useEffect(() => {
    let filtered = [...quotations];
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(q => q.status === statusFilter);
    }
    
    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter(
        q =>
          q.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (q.quotationNumber && q.quotationNumber.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (sortOption === "newest") {
        return new Date(b.createdAt || b.weddingDate) - new Date(a.createdAt || a.weddingDate);
      } else if (sortOption === "oldest") {
        return new Date(a.createdAt || a.weddingDate) - new Date(b.createdAt || b.weddingDate);
      } else if (sortOption === "highest") {
        return b.total - a.total;
      } else {
        return a.total - b.total;
      }
    });
    
    setFilteredQuotations(filtered);
  }, [searchTerm, quotations, statusFilter, sortOption]);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await fetch('http://priyanthabandara.lk/get_quotations.php');
      const serverQuotations = await response.json();
      
      const processedQuotations = serverQuotations.map(q => ({
        ...q,
        total: formatTotal(q.total),
        balance: formatTotal(q.balance || q.total),
        status: q.status || 'pending',
        createdAt: q.createdAt || new Date().toISOString()
      }));
      
      setQuotations(processedQuotations);
    } catch (error) {
      console.error("Error loading quotations:", error);
      alert('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const formatTotal = (total) => {
    if (typeof total === 'number') return total;
    if (typeof total === 'string') {
      const num = parseFloat(total.replace(/[^0-9.]/g, ''));
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

  const handleDelete = async (quotation) => {
    if (window.confirm("Are you sure you want to delete this quotation?")) {
      setDeletingId(quotation.filename);
      try {
        const response = await fetch(
          `http://priyanthabandara.lk/delete_quotation.php?filename=${encodeURIComponent(quotation.filename)}`,
          { method: 'DELETE' }
        );
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to delete quotation');
        }
        
        setQuotations(prev => prev.filter(q => q.filename !== quotation.filename));
        alert('Quotation deleted successfully');
      } catch (error) {
        console.error("Deletion error:", error);
        alert(`Failed to delete quotation: ${error.message}`);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleView = (quotation) => {
    setEditingQuotation(quotation);
  };

  const handleStatusChange = async (quotation, newStatus) => {
    try {
      setUpdatingStatus(quotation.filename);
      
      const response = await fetch('http://priyanthabandara.lk/update_quotation_status.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: quotation.filename,
          status: newStatus
        })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update status');
      }
      
      setQuotations(prev => prev.map(q => 
        q.filename === quotation.filename ? { ...q, status: newStatus } : q
      ));
    } catch (error) {
      console.error("Status update error:", error);
      alert(`Failed to update status: ${error.message}`);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10B981';
      case 'discarded': return '#EF4444';
      default: return '#1665e7'; // pending
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'completed': return 'rgba(16, 185, 129, 0.1)';
      case 'discarded': return 'rgba(239, 68, 68, 0.1)';
      default: return '#63ace01a'; // pending
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (editingQuotation) {
    return (
      <div className="editing-container">
        <div className="editing-card">
          <h2>Editing Quotation for {editingQuotation.customerName}</h2>
          <p>In a complete application, this would redirect to the quotation maker tab with all data pre-filled.</p>
          <button 
            onClick={() => setEditingQuotation(null)}
            className="back-button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            Back to History
          </button>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading quotations...</p>
    </div>
  );



const handleEdit = (quotation) => {
  // Ensure we have all necessary fields
  const quotationToEdit = {
    ...quotation,
    editablePackages: quotation.editablePackages || initialPackageOptions,
    payments: quotation.payments || [],
    homecomingOptions: quotation.homecomingOptions || {
      shootOnly: false,
      shootFunction: false
    },
    photographySetup: quotation.photographySetup || {
      camera1: false,
      camera2: false
    },
    cinematographySetup: quotation.cinematographySetup || {
      camera1: false,
      camera2: false,
      drone: false
    },
    preshootSetup: quotation.preshootSetup || {
      camera1: false,
      camera2: false,
      drone: false
    }
  };
  
  localStorage.setItem('quotationToEdit', JSON.stringify(quotationToEdit));
  window.location.href = 'http://priyanthabandara.lk/dashboard/maker';
};



  const initialPackageOptions = {
    "Basic Package": [
      "Main photoshoot at preferred location (Two camera coverage)",
      "Wedding ceremony and reception",
      "2 enlargement 16x24 (with frame)",
      "2 enlargement 12x18 (with frame)",
      "Photobook Albums 12x30 or 16x24 (Magazine type 50 pages)",
      "100 Thanking card 6x8 (one side)",
      "Digital album",
      "10 Edited images for social media",
      "Wedding day all photos soft copies free",
      "Colombo area transport free",
      "Videography",
    ],
    "Standard Package": [
      "Main photoshoot at preferred location (Two camera coverage)",
      "Wedding ceremony and reception",
      "2 enlargement 20x30 (with frame)",
      "2 enlargement 16x24 (with frame)",
      "Photobook Albums 12x30 or 16x24 (Story type 50 pages)",
      "150 Thanking card 6x8 (one side)",
      "Digital album",
      "10 high-end edited images for social media",
      "Wedding day all photos soft copies free",
      "Colombo area transport free",
      "Videography",
    ],
    "Premium Package": [
      "Main photoshoot at preferred location (Two camera coverage)",
      "Wedding ceremony and reception (Two camera coverage)",
      "2 enlargement 20x30 (with frame)",
      "2 enlargement 16x24 (with frame)",
      "2 enlargement 12x18 (with frame)",
      "Photobook Albums 12x34 or 17x24 (Magazine type TAB album 60 pages)",
      "150 Thanking card 6x8 (double side)",
      "Digital album",
      "15 high-end edited images for social media",
      "Wedding day all photos soft copies free",
      "Colombo area transport free",
      "Videography",
    ]
  };

  return (
    <div className="quotation-history-container">
      <div className="history-header">
        <div className="header-content">
          <h1>Quotation History</h1>
          <p>Manage and review all your quotations in one place</p>
        </div>
      </div>

      <div className="controls-section">
        <div className="search-filter-container">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input
              type="text"
              placeholder="Search by customer or quotation #..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filters-container">
            <div className="filter-group">
              <label>Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="discarded">Discarded</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Sort By</label>
              <select 
                value={sortOption} 
                onChange={(e) => setSortOption(e.target.value)}
                className="filter-select"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="highest">Highest Amount</option>
                <option value="lowest">Lowest Amount</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {filteredQuotations.length === 0 ? (
        <div className="empty-state">
          <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
          <h3>No quotations found</h3>
          <p>{quotations.length === 0 
            ? 'You haven\'t created any quotations yet.' 
            : 'No quotations match your current filters.'}
          </p>
        </div>
      ) : (
        <div className="quotations-list">
          <div className="list-header">
            <div className="header-cell">Quotation No.</div>
            <div className="header-cell">Customer</div>
            <div className="header-cell">Event Date</div>
            <div className="header-cell">Total Amount</div>
            <div className="header-cell">Payed Amount</div>
            <div className="header-cell">Pending Amount</div>
            <div className="header-cell">Status</div>
            <div className="header-cell">Actions</div>
          </div>

          {filteredQuotations.map((quotation) => (
            <div 
              key={quotation.filename} 
              className="quotation-card"
              style={{ borderLeft: `4px solid ${getStatusColor(quotation.status)}` }}
            >
              <div className="card-cell quotation-number1">
                <span className="label-mobile">Quotation #</span>
                <span className="value">#{quotation.quotationNumber || quotation.filename}</span>
              </div>
              
              <div className="card-cell customer">
                <span className="label-mobile">Customer</span>
                <div className="customer-details">
                  <span className="customer-name">{quotation.customerName || "Unnamed Quotation"}</span>
                  <span className="customer-email">{quotation.email || "No email provided"}</span>
                </div>
              </div>
              
              <div className="card-cell date">
                <span className="label-mobile">Event Date</span>
                <span className="value">{formatDate(quotation.weddingDate)}</span>
              </div>
              
              <div className="card-cell total">
                <span className="label-mobile">Total Amount</span>
                <span className="value">Rs. {quotation.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>

             <div className="card-cell total">
  <span className="label-mobile">Payed Amount</span>
  <span className="value">
    Rs. {(quotation.total - (quotation.balance || 0)).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}
  </span>
</div>
              
              <div className="card-cell pending">
                <span className="label-mobile">Due Amount</span>
                <span className="value">Rs. {quotation.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              
              <div className="card-cell status">
                <span className="label-mobile">Status</span>
                <select
                  value={quotation.status}
                  onChange={(e) => handleStatusChange(quotation, e.target.value)}
                  className="status-select"
                  disabled={updatingStatus === quotation.filename}
                  style={{ 
                    backgroundColor: getStatusBgColor(quotation.status),
                    color: getStatusColor(quotation.status),
                    borderColor: getStatusColor(quotation.status)
                  }}
                >
                  {updatingStatus === quotation.filename ? (
                    <option>Updating...</option>
                  ) : (
                    <>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="discarded">Discarded</option>
                    </>
                  )}
                </select>
              </div>
              
              <div className="card-cell actions">
  <div className="action-buttons">
    <button 
      onClick={() => handleEdit(quotation)}
      className="action-buttonx view-button"
      title="View/Edit"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
      </svg>
    </button>
    
    <button 
      onClick={() => handleDelete(quotation)}
      className="action-buttonx delete-button"
      disabled={deletingId === quotation.filename}
      title="Delete"
    >
      {deletingId === quotation.filename ? (
        <div className="button-spinner"></div>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
        </svg>
      )}
    </button>
  </div>
</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default QuotationHistory;